import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, BookOpen, Bookmark, TrendingUp, Settings as SettingsIcon, LogIn, LogOut, Languages, ShieldAlert, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export const AppShell = ({ children, hideNav = false, hideHeader = false }) => {
  const { user, logout } = useAuth();
  const { lang, toggle, tr } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div className="wpe-shell" data-testid="app-shell">
      {!hideHeader && (
        <header
          className="sticky top-0 z-40 backdrop-blur-md border-b border-[rgb(var(--wpe-border))] px-4 py-3 flex items-center justify-between"
          style={{ background: "rgba(248, 250, 252, 0.92)" }}
        >
          <Link to={user ? "/home" : "/"} className="flex items-center gap-2" data-testid="brand-link">
            <span className="wpe-brand-mark" aria-hidden>LT</span>
            <span className="flex items-baseline gap-1">
              <span className="text-[15px] font-bold tracking-tight text-[rgb(var(--wpe-navy))]">
                {tr("brand")}
              </span>
              <span className="text-[10px] text-[rgb(var(--wpe-muted))] hidden sm:inline">· {tr("v1Version")}</span>
            </span>
          </Link>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="wpe-ghost text-xs"
              onClick={toggle}
              data-testid="language-toggle-btn"
              aria-label={tr("langToggleAria")}
              title={tr("langToggleAria")}
            >
              <Languages size={14} aria-hidden />
              <span className="inline-flex items-baseline gap-0.5" aria-hidden>
                <span
                  className={
                    lang === "es"
                      ? "font-bold text-[rgb(var(--wpe-navy))]"
                      : "text-[rgb(var(--wpe-muted))]"
                  }
                  data-testid="language-toggle-es"
                >
                  ES
                </span>
                <span className="text-[rgb(var(--wpe-muted))]">/</span>
                <span
                  className={
                    lang === "en"
                      ? "font-bold text-[rgb(var(--wpe-navy))]"
                      : "text-[rgb(var(--wpe-muted))]"
                  }
                  data-testid="language-toggle-en"
                >
                  EN
                </span>
              </span>
            </button>
            {user?.is_admin && !isActive("/admin") && (
              <button
                type="button"
                className="wpe-ghost text-xs text-[rgb(var(--wpe-teal-hover))]"
                onClick={() => navigate("/admin")}
                data-testid="header-admin-link"
                title={tr("adminEntryLabel")}
              >
                <ShieldCheck size={14} />
                <span className="hidden sm:inline">{tr("adminEntryLabel")}</span>
              </button>
            )}
            {user ? (
              <button type="button" className="wpe-ghost text-xs" onClick={() => { logout(); navigate("/"); }} data-testid="signout-btn">
                <LogOut size={14} /> {tr("signOut")}
              </button>
            ) : (
              <button type="button" className="wpe-ghost text-xs" onClick={() => navigate("/auth")} data-testid="signin-btn">
                <LogIn size={14} /> {tr("signIn")}
              </button>
            )}
          </div>
        </header>
      )}

      <main className="flex-1 pb-24 sm:pb-8">{children}</main>

      {!hideNav && user && (
        <nav
          className="fixed bottom-0 left-0 right-0 mx-auto max-w-md sm:max-w-lg border-t border-[rgb(var(--wpe-border))] bg-white flex items-stretch"
          data-testid="bottom-nav"
        >
          <NavTab to="/home" active={isActive("/home")} icon={<Home size={18} />} label={tr("tabHome")} testid="nav-home-btn" />
          <NavTab to="/practice" active={isActive("/practice") || isActive("/briefing") || isActive("/session")} icon={<BookOpen size={18} />} label={tr("tabPractice")} testid="nav-scenarios-btn" />
          <NavTab to="/phrases" active={isActive("/phrases")} icon={<Bookmark size={18} />} label={tr("tabPhrases")} testid="nav-phrases-btn" />
          <NavTab to="/progress" active={isActive("/progress")} icon={<TrendingUp size={18} />} label={tr("tabProgress")} testid="nav-progress-btn" />
          <NavTab to="/settings" active={isActive("/settings")} icon={<SettingsIcon size={18} />} label={tr("tabSettings")} testid="nav-settings-btn" />
        </nav>
      )}
    </div>
  );
};

const NavTab = ({ to, active, icon, label, testid }) => (
  <Link
    to={to}
    className={`flex-1 py-2.5 flex flex-col items-center gap-0.5 text-[10px] ${active ? "text-[rgb(var(--wpe-teal))]" : "text-[rgb(var(--wpe-muted))]"}`}
    data-testid={testid}
  >
    {icon}
    {label}
  </Link>
);

export const PilotDisclaimer = () => {
  const { tr } = useLang();
  return (
    <div
      className="flex items-start gap-2 text-[11px] text-[rgb(var(--wpe-muted))] px-3 py-2 rounded-md bg-white/60 border border-[rgb(var(--wpe-border))]"
      data-testid="pilot-disclaimer"
    >
      <ShieldAlert size={14} className="text-[rgb(var(--wpe-amber))] mt-0.5 shrink-0" />
      <span>{tr("disclaimer")}</span>
    </div>
  );
};
